import { DateTime } from '@mode-switch/express';
import { Names, Values } from '@mode-switch/model';
import {
  BatchEncoder,
  EncChannel,
  EncField,
  EncFilterField,
  Encoding,
  EncodingResult,
  FilterType,
  Guide,
  MarkChannel,
  SelectionMode,
  SortType,
  VariableType,
  DataType as VegasDataType,
} from '@mode-vegas/vegas';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { LegacyReportFilterControl } from '@mode/filters/util';
import { ChartState } from '@mode/legacy/types-app-state';
import { ChartTypes, FlamingoTypes, ReportTypes } from '@mode/shared/contract-common';
import { CompiledField, CompiledFormula, FormulaOperations, Switch } from '@mode/shared/util-switch';

export enum ZoneType {
  Discrete = 'DISCRETE',
  Continuous = 'CONTINUOUS',
  Aggregate = 'AGGREGATE',
}

export const hChartTypes = new Set<ChartTypes.ChartType>(['hStackedBar', 'hBar', 'hStackedBar100']);

const guide = new Guide();

export function getFirstFieldInChannel(enc: Encoding, channel: MarkChannel, series?: string): EncField {
  if (enc == null) {
    return null;
  }

  if (series != null) {
    const seriesMarks = enc.marks.series.find((s) => s.field === series);
    if (seriesMarks != null) {
      return seriesMarks.properties[channel].values[0];
    }
  } else {
    return enc.marks.properties[channel].values[0];
  }

  return null;
}

function buildDefaultSystemField(field: FlamingoTypes.SystemField): EncField {
  if (Switch.Fields.isSystem(field)) {
    return {
      formula: field.formula.source,
      variable: { type: Switch.Fields.isValues(field) ? VariableType.CONTINUOUS : VariableType.DISCRETE },
    };
  }
}

/**
 * Remove filters that have no effect
 */
export function removeNonActiveFilters(
  filters: ReadonlyArray<Readonly<EncFilterField>>
): ReadonlyArray<Readonly<EncFilterField>> {
  // currently we expect filters in the ALL or INCLUDE Selection Mode, exclude is a valid mode
  // but we treat all filters as include
  return filters.filter((filter) => {
    switch (filter.filterType) {
      case FilterType.Discrete:
      case FilterType.DiscreteBoolean:
        return filter.selection.mode !== SelectionMode.ALL && filter.selection.selected.length > 0;

      case FilterType.Continuous:
      case FilterType.ContinuousDate:
        return filter.range.min || filter.range.max;

      default:
        return true;
    }
  });
}

/**
 * Adds a field to the encoding with default settings based on ZoneType and channel
 */
function addField(
  encoder: BatchEncoder,
  encResult: EncodingResult,
  field: CompiledFormula,
  channel: EncChannel,
  series: string | undefined,
  at?: string,
  zoneType?: ZoneType
) {
  const { encoding, summary } = encResult;

  if (!(encoder instanceof BatchEncoder)) {
    encoder = guide.newBatchOperation(encoding);
  }

  let ids: string[] = [];

  switch (channel) {
    case 'value': {
      if (summary.color.length > 0 && summary.color[0].formula !== Names.Key) {
        // For a non-Measure Names color, we need to replace the current value(s).
        // There can only be on field in value in this case.
        encoder.removeFields(encoding.value.map((f) => f.id));
      }
      encoder.addEncField(EncodingHelpers.buildDefaultEncField({ field, zone: ZoneType.Aggregate }), channel, at);
      break;
    }
    case 'row': // falls through
    case 'column': {
      // for when dropping into the "X" axis dropzone
      const fields = channel === 'row' ? encoding.y : encoding.x;
      const discreteFields = channel === 'row' ? encoding.row : encoding.column;

      zoneType = zoneType || ZoneType.Continuous;
      if (fields.length && fields[0].formula === Names.Key) {
        if (discreteFields.length) {
          encoder.removeField(discreteFields[0].id);
        }
        at = fields[0].id; // need this maintain placement of measure names
        zoneType = ZoneType.Discrete;
      } else {
        if (fields.length) {
          encoder.removeField(fields[0].id);
        }
      }

      encoder.addEncField(EncodingHelpers.buildDefaultEncField({ field, zone: zoneType }), channel, at);
      break;
    }
    case 'color':
      if (summary.color.length) {
        ids = summary.colorIds[summary.color[0].id];
      }

      encoder.removeFields(ids);
      encoder.addEncFieldToSeries(
        EncodingHelpers.buildDefaultEncField({ field, zone: ZoneType.Discrete }),
        channel,
        series
      );
      break;
    case 'angle':
      if (summary.angle.length) {
        ids = summary.angleIds[summary.angle[0].id];
      }

      encoder.removeFields(ids);
      encoder.addEncFieldToSeries(
        EncodingHelpers.buildDefaultEncField({ field, zone: ZoneType.Aggregate }),
        channel,
        series
      );
      break;
    case 'size':
      if (summary.size.length) {
        ids = summary.sizeIds[summary.size[0].id];
      }

      encoder.removeFields(ids);
      encoder.addEncFieldToSeries(
        EncodingHelpers.buildDefaultEncField({ field, zone: ZoneType.Aggregate }),
        channel,
        series
      );
      break;
    case 'baseline':
      if (summary.baseline.length) {
        ids = summary.baselineIds[summary.baseline[0].id];
      }

      encoder.removeFields(ids);
      encoder.addEncFieldToSeries(
        EncodingHelpers.buildDefaultEncField({ field, zone: ZoneType.Aggregate }),
        channel,
        series
      );
      break;
    case 'tooltip':
      encoder.addEncFieldToSeries(EncodingHelpers.buildDefaultEncField({ field, zone: zoneType }), channel, series);
      break;
    case 'detail': // falls through
    case 'text':
      // TEMPORARY: For big values, limit text/detail to just one. Pivot tables will hopefully only need measure values here.
      if (encoding.marks.properties[channel].values.length > 0) {
        encoder.removeField(encoding.marks.properties[channel].values[0].id);
      }

      if (zoneType != null) {
        encoder.addEncFieldToSeries(EncodingHelpers.buildDefaultEncField({ field, zone: zoneType }), channel, '*', at);
      } else {
        encoder.addEncFieldToSeries(EncodingHelpers.buildDefaultEncField({ field }), channel, '*', at);
      }
      break;
  }
}

/**
 * Moves an existing field to another channel in the encoding and possibly
 * transform the formula based on our chart builder rules
 */
function moveField(
  encoder: BatchEncoder,
  enc: Encoding,
  field: CompiledField,
  channel: EncChannel,
  series: string | undefined,
  at?: string
) {
  switch (channel) {
    case 'row':
      // Handle horizontal bar chart moving a field into row
      if (enc.x.length && enc.x[0].formula === Values.Key) {
        if (enc.y.length && enc.y[0].formula === Names.Key) {
          if (enc.row.length) {
            encoder.removeField(enc.row[0].id);
          }

          encoder.updateField({
            ...EncodingHelpers.buildUpdateFieldParams(field, ZoneType.Discrete),
            channel: channel,
            at: enc.y[0].id,
          });
        } else {
          if (enc.y.length) {
            encoder.removeField(enc.y[0].id);
          }

          encoder.updateField({
            ...EncodingHelpers.buildUpdateFieldParams(field, ZoneType.Continuous),
            channel: channel,
          });
        }
        break;
      } else if (enc.marks.channel === '*') {
        // Assume big value
        if (enc.y.length) {
          encoder.removeField(enc.y[0].id);
        }

        encoder.updateField({
          ...EncodingHelpers.buildUpdateFieldParams(field, ZoneType.Discrete),
          channel: channel,
        });
        break;
      }
    // falls through otherwise
    case 'value':
      encoder.updateField({
        ...EncodingHelpers.buildUpdateFieldParams(field, ZoneType.Aggregate),
        at,
        channel: 'value',
      });
      break;
    case 'column':
      if (enc.x.length && enc.x[0].formula === Names.Key) {
        if (enc.column.length) {
          encoder.removeField(enc.column[0].id);
        }

        encoder.updateField({
          ...EncodingHelpers.buildUpdateFieldParams(field, ZoneType.Discrete),
          channel: channel,
          at: enc.x[0].id,
        });
      } else {
        if (enc.x.length) {
          encoder.removeField(enc.x[0].id);
        }

        encoder.updateField({
          ...EncodingHelpers.buildUpdateFieldParams(field, ZoneType.Continuous),
          channel: channel,
        });
      }
      break;
    // TODO: These may need to be updated to move all series fields in the summary
    case 'color':
      encoder.updateField({
        ...EncodingHelpers.buildUpdateFieldParams(field, ZoneType.Discrete),
        channel: channel,
        series: enc.marks.series.length ? enc.marks.series[0].field : series,
      });
      break;
    case 'size':
    case 'angle':
    case 'baseline':
      encoder.updateField({
        ...EncodingHelpers.buildUpdateFieldParams(field, ZoneType.Aggregate),
        channel: channel,
        series: enc.marks.series.length ? enc.marks.series[0].field : series,
      });
      break;
    case 'tooltip':
      encoder.updateField({
        id: field.alias,
        channel: channel,
        series: enc.marks.series.length ? enc.marks.series[0].field : series,
      });
      break;
    case 'text': // This is limited to just big value support
      if (enc.marks.properties[channel].values.length > 0) {
        encoder.removeField(enc.marks.properties[channel].values[0].id);
      }

      const modifyField = EncodingHelpers.buildUpdateFieldParams(field, ZoneType.Aggregate);
      encoder.updateField({
        ...modifyField,
        channel: channel,
        series: enc.marks.series.length ? enc.marks.series[0].field : series,
      });
      break;
  }
}

// Warning, will not overwrite the same filters, since there's no easy way to figure that out.
// So don't call this more than once on the same encoding.
export function addReportFiltersToChartEncoding(
  view: FlamingoTypes.ModeView | undefined,
  encoding: Encoding,
  filters: LegacyReportFilterControl[],
  merge = false
) {
  if (filters == null || filters.length <= 0) {
    return encoding;
  }

  const encoder = guide.newBatchOperation(encoding);
  const encFilters = filters
    .map<EncFilterField>((filter) => {
      switch (filter.filterType) {
        case ReportTypes.ReportFilterType.Range:
          if (filter.isDateType) {
            return {
              formula: filter.formula,
              variable: { type: VariableType.CONTINUOUS },
              filterType: FilterType.ContinuousDate,
              range: { max: filter.selectedAsDates.max, min: filter.selectedAsDates.min },
              includeMin: true,
              includeMax: false,
              includeNulls: filter.includeNulls,
            };
          } else {
            return {
              formula: filter.formula,
              variable: { type: VariableType.CONTINUOUS },
              filterType: FilterType.Continuous,
              range: { max: filter.selected.max, min: filter.selected.min },
              includeMin: true,
              includeMax: true,
              includeNulls: filter.includeNulls,
            };
          }
        case ReportTypes.ReportFilterType.Multi:
          if (!filter.selected.all) {
            return {
              formula: filter.formula,
              variable: { type: VariableType.DISCRETE },
              filterType: FilterType.Discrete,
              selection: {
                selected: filter.selected.values.toArray(),
                mode: SelectionMode.INCLUDE,
              },
            };
          }
          break;
        case ReportTypes.ReportFilterType.Single:
          if (!filter.selected.all) {
            if (filter.dataType === VegasDataType.BOOLEAN) {
              return {
                formula: filter.formula,
                variable: { type: VariableType.DISCRETE },
                filterType: FilterType.DiscreteBoolean,
                selection: {
                  selected: [filter.selected.value],
                  mode: SelectionMode.INCLUDE,
                },
              };
            } else {
              return {
                formula: filter.formula,
                variable: { type: VariableType.DISCRETE },
                filterType: FilterType.Discrete,
                selection: {
                  selected: [filter.selected.value],
                  mode: SelectionMode.INCLUDE,
                },
              };
            }
          }
          break;
      }
    })
    .filter((f) => f != null);

  // eslint-disable-next-line no-eq-null, eqeqeq
  if (view != null && merge) {
    for (const filter of encoding.filter) {
      /**
       * Ensure relative date filters have the correct w/y start values before attempting
       * to merge in report filters. When combining date filters, Vegas converts relative
       * date filters to continuous date filters such that the resulting filter is a simple
       * intersection of the two ranges; we provide the workspace w/y config for relative
       * date filters that don't have one or both defined
       */
      if (filter.filterType === FilterType.RelativeDate) {
        const yearStart = DateTime.yearStartMonth(filter.yearStart ?? view.yearStart);
        const weekStart = DateTime.weekStartDow(filter.weekStart ?? view.weekStart);
        // "addEncFilter" is an upsert
        encoder.addEncFilter({
          ...filter,
          yearStart,
          weekStart,
        });
      }
    }
  }

  encFilters.forEach((f) => {
    if (merge) {
      encoder.mergeEncFilter(f);
    } else {
      encoder.appendEncFilter(f);
    }
  });

  return encoder.complete().encoding;
}

export function addOrReplaceField(
  result: EncodingResult,
  field: CompiledFormula,
  channel: EncChannel,
  at?: string,
  zoneType?: ZoneType
) {
  const encoder = guide.newBatchOperation(result.encoding);
  const series = result.encoding.marks.series.length ? undefined : '*';

  addField(encoder, result, field, channel, series, at, zoneType);

  return encoder.complete();
}

export function updateOrMoveField(
  result: EncodingResult,
  field: CompiledField,
  channel: EncChannel,
  at?: string,
  zoneType?: ZoneType
) {
  const encoder = guide.newBatchOperation(result.encoding);

  const enc = result.encoding;
  const summary = result.summary;
  const series = enc.marks.series.length ? undefined : '*';

  if (field.isSystem) {
    encoder.updateField({ id: field.alias, channel: channel });
  } else {
    moveField(encoder, enc, field, channel, series, at);
    // Check if we've moved a color field and readd measure names if needed
    if (summary.color.some((c) => c.id === field.alias) && channel !== 'color') {
      if (enc.marks.channel !== '*' && enc[enc.marks.channel][0].formula === Values.Key) {
        // Add names back to color
        encoder.addEncFieldToSeries(buildDefaultSystemField(Switch.Fields.names()), 'color');
      }
    }
  }

  return encoder.complete();
}

export function addTrendToEncoding(
  view: FlamingoTypes.ModeView,
  enc: Encoding,
  field: CompiledFormula,
  channel: EncChannel,
  at?: string
) {
  const encoder = guide.newBatchOperation(enc);

  if (field.isField === true) {
    if (field.isValid) {
      const diffCompiled = field.withNewFormula(FormulaOperations.diff(field));

      // Assume we're moving a field
      const series = enc.marks.series.length ? undefined : '*';
      moveField(encoder, enc, diffCompiled, channel, series, at);
    }
  } else if (field.isField === false) {
    const pctCompiled = field.withNewFormula(FormulaOperations.pctDiff(field));
    addFormulaToEncoding(
      encoder,
      { formula: pctCompiled.formula, variable: { type: VariableType.CONTINUOUS } },
      channel,
      at
    );
  }

  return encoder.complete();
}

function addFormulaToEncoding(encoder: BatchEncoder, encField: EncField, channel: EncChannel, at?: string) {
  let ids: string[] = [];
  const { encoding, summary } = encoder.complete();
  const series = encoding.marks.series.length ? undefined : '*';

  switch (channel) {
    case 'value':
      encoder.addEncField(encField, channel, at);
      break;
    case 'row': // falls through
    case 'column': // for when dropping into the "X" axis dropzone
      const fields = channel === 'row' ? encoding.y : encoding.x;
      const discreteFields = channel === 'row' ? encoding.row : encoding.column;

      if (fields.length && fields[0].formula === Names.Key) {
        if (discreteFields.length) {
          encoder.removeField(discreteFields[0].id);
        }
        at = fields[0].id; // need this maintain placement of measure names
      } else {
        if (fields.length) {
          encoder.removeField(fields[0].id);
        }
      }

      encoder.addEncField(encField, channel, at);
      break;
    case 'color':
      if (summary.color.length) {
        ids = summary.colorIds[summary.color[0].id];
      }

      encoder.removeFields(ids);
      encoder.addEncFieldToSeries(encField, channel, series);
      break;
    case 'angle':
      if (summary.angle.length) {
        ids = summary.angleIds[summary.angle[0].id];
      }

      encoder.removeFields(ids);
      encoder.addEncFieldToSeries(encField, channel, series);
      break;
    case 'size':
      if (summary.size.length) {
        ids = summary.sizeIds[summary.size[0].id];
      }

      encoder.removeFields(ids);
      encoder.addEncFieldToSeries(encField, channel, series);
      break;
    case 'baseline':
      if (summary.baseline.length) {
        ids = summary.baselineIds[summary.baseline[0].id];
      }

      encoder.removeFields(ids);
      encoder.addEncFieldToSeries(encField, channel, series);
      break;
    case 'tooltip':
      encoder.addEncFieldToSeries(encField, channel, series);
      break;
    case 'detail': // falls through
    case 'text':
      // TEMPORARY: For big values, limit text/detail to just one. Pivot tables will hopefully only need measure values here.
      if (encoding.marks.properties[channel].values.length > 0) {
        encoder.removeField(encoding.marks.properties[channel].values[0].id);
      }

      encoder.addEncFieldToSeries(encField, channel, '*', at);
      break;
  }
}

export function quickSortDesc(
  selectedChart: ChartState,
  view: FlamingoTypes.ModeView,
  descending: boolean,
  allowNested = false
) {
  const {
    canSort,
    chartType,
    encResult: { encoding },
  } = selectedChart;

  const sortFields = EncodingHelpers.findSortByFields(encoding, chartType);
  const orderText = descending ? 'Descending' : 'Ascending';
  const isVegas = chartType === 'vegas';
  const isBarChart = chartType === 'bar' || chartType === 'hBar';
  const numOfMeasures = encoding.value.length;
  const defaultTooltipMessage = `Sort ${orderText}`;

  if (sortFields) {
    const sortedViewField = CompiledField.fromEncField(view, sortFields.field);

    if (sortFields.by) {
      const byViewField = CompiledField.fromEncField(view, sortFields.by);

      let primaryLabel = encoding.marks.channel === 'x' ? 'X-Axis' : 'Y-Axis';
      if (isVegas) {
        primaryLabel = byViewField.displayName;
      }

      if (isBarChart && !canSort && numOfMeasures === 1) {
        // This is a specific flow in which we know that the user must add one additional measure to the graph
        // in order for the grouped bar chart to properly sort.
        return `Add another series in ${primaryLabel.toLowerCase()} or color to enable sorting within groups`;
      } else if (isBarChart && !canSort && numOfMeasures === 0) {
        // The user has not yet put any measures in the chart builder, and as such we want to show the default
        // tooltip text.
        return defaultTooltipMessage;
      }

      let within = '';
      if (allowNested) {
        const sort = EncodingHelpers.createNestedOrByFieldSort(encoding, sortFields.field, sortFields.by, descending);
        if (sort.type === SortType.NESTED) {
          const outer = EncodingHelpers.getBasicOuterFields(encoding, sortFields.field);
          if (outer.length !== 0) {
            within =
              ' within ' +
              outer
                .map((f) => CompiledField.fromEncField(view, f))
                .map((f) => f.displayName)
                .join(', ');
          }
        }
      }

      return `Sort ${sortedViewField.displayName} ${orderText}${within} by ${
        byViewField.isValues ? primaryLabel : byViewField.displayName
      }`;
    }

    return `Sort ${sortedViewField.displayName} ${orderText}`;
  }

  return defaultTooltipMessage;
}
